import { useParams } from 'react-router';
import { Box, Typography } from '@mui/material';
import { MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
  BackButton,
  formatCategoriesWithBullet,
  LoadingIndicator,
  useLocale,
  EmbeddedView,
  formatArticleDetailDate,
  defaultHTMLIframe,
} from '@xolv-mybrightlink/common';
import { FormattedMessage } from 'react-intl';
import { fixHyperlinkOnClickHandler } from '../common/utils';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import { useBookmarkArticle } from '../hooks/useBookmarkArticle';
import { useFetchArticleDetails } from '../hooks/useFetchArticleDetails';

export const ArticleDetailPage = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const locale = useLocale();
  const [articleContent, setArticleContent] = useState('');
  const { articleSlug, categoryId = 'all-articles' } = useParams<{
    articleSlug: string;
    categoryId: string;
  }>();
  const articleDetail = useFetchArticleDetails(articleSlug);
  const bookmarkArticle = useBookmarkArticle();

  useEffect(() => {
    if (typeof articleDetail !== 'string') {
      const doc = new DOMParser().parseFromString(
        articleDetail.content,
        'text/html',
      );
      setArticleContent(fixHyperlinkOnClickHandler(doc));
    }
  }, [articleDetail]);

  const formattedArticleContent = useMemo<string>(() => {
    return defaultHTMLIframe(articleContent);
  }, [articleContent]);

  const onBookmarkClicked = async (event: MouseEvent<SVGSVGElement>) => {
    if (!articleDetail || typeof articleDetail === 'string') {
      return;
    }
    event.stopPropagation();
    await bookmarkArticle(articleDetail.databaseId, categoryId);
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        height: '100%',
        background: 'white',
      }}
    >
      <Box
        sx={{
          pt: 10,
          pl: 10,
        }}
      >
        <BackButton />
      </Box>
      {typeof articleDetail !== 'string' && (
        <Box>
          <Box
            sx={{
              p: 10,
              background: 'white',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}
            >
              <Typography
                variant={'h4'}
                sx={{
                  fontWeight: 500,
                }}
              >
                {articleDetail.title}
              </Typography>
              {articleDetail.isSaved ? (
                <BookmarkIcon
                  sx={{
                    color: '#307fe2',
                    width: 40,
                    height: 40,
                    cursor: 'pointer',
                  }}
                  onClick={onBookmarkClicked}
                />
              ) : (
                <BookmarkBorderIcon
                  sx={{
                    color: '#307fe2',
                    width: 40,
                    height: 40,
                    cursor: 'pointer',
                  }}
                  onClick={onBookmarkClicked}
                />
              )}
            </Box>
            <Typography
              sx={{
                fontSize: 16,
                color: '#626a7a',
                pt: 5,
              }}
            >
              {`Sources: ${articleDetail.article.sourceCredit}`}
            </Typography>
            <Typography
              sx={{
                fontSize: 16,
                color: '#626a7a',
                pt: 2.5,
              }}
            >
              {`Published on ${formatArticleDetailDate(
                articleDetail.date,
                locale,
              )}`}
            </Typography>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 500,
                pt: 5,
              }}
            >
              {formatCategoriesWithBullet(articleDetail.categories.nodes)}
            </Typography>
            <Box
              component='img'
              sx={{
                width: '100%',
                mt: 5,
                height: 400,
                borderRadius: 3,
                boxShadow:
                  '0 12px 16px 0 rgba(59, 69, 89, 0.05), 0 8px 10px 0 rgba(59, 69, 89, 0.1)',
                objectFit: 'cover',
              }}
              src={articleDetail.featuredImage.node.sourceUrl}
              alt={articleDetail.title}
            />
            <EmbeddedView html={formattedArticleContent} bodyMargin='8px 0px' />
            <Typography
              sx={{
                fontSize: 16,
                pt: 10,
                color: '#626a7a',
              }}
            >
              {formatCategoriesWithBullet(articleDetail.tags.nodes)}
            </Typography>
          </Box>
          <Box
            sx={{
              height: 20,
              background: '#f7f8fb',
            }}
          />
          <Box
            sx={{
              p: 10,
              background: 'white',
            }}
          >
            <Typography
              sx={{
                fontSize: 16,
                color: '#626a7a',
              }}
            >
              <FormattedMessage id='resources.article.own-opinions' />
            </Typography>
            <Typography
              sx={{
                pt: 5,
                fontSize: 16,
                color: '#626a7a',
              }}
            >
              {articleDetail.article.copyright}
            </Typography>
          </Box>
        </Box>
      )}
      {articleDetail === 'loading' && (
        <LoadingIndicator
          sx={{
            height: '100%',
          }}
        />
      )}
    </Box>
  );
};
